/**
 * Display name for Work patient pickers.
 *
 * Linked sessions overwrite ``session.patient.name`` with first_name only
 * (``PatientProfileModel.to_legacy_patient_model``). The projected
 * ``patient_profile`` still has first + last; prefer that, then the
 * identifier, then the embedded snapshot.
 *
 * Same order as ``getFullName`` (human name). ``getDisplayName`` prefers
 * ``demographic_string`` (often "First Last, M, 32") which is the wrong
 * grain for a glance picker.
 */

import { patientNameWithoutDemographics } from './patient-display-name-internal';

export type PatientNameFields = {
  first_name?: string | null;
  last_name?: string | null;
  demographic_string?: string | null;
};

export const resolvePatientListName = (
  profile: PatientNameFields | null | undefined,
  embeddedName?: string | null
): string | undefined => {
  const full = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim();
  const identifier = profile?.demographic_string?.trim();
  const embedded = embeddedName?.trim();
  return full || identifier || embedded || undefined;
};

/** The name Work shows for a patient: the list name without an identifier's demographics. */
export const resolvePatientLabel = (
  profile: PatientNameFields | null | undefined,
  embeddedName?: string | null
): string | undefined => {
  const name = resolvePatientListName(profile, embeddedName);
  return name ? patientNameWithoutDemographics(name) || undefined : undefined;
};

/** Avatar colour seed, the same identifier-first order as the patients domain's `getDisplayName`. */
export const resolvePatientAvatarSeed = (
  profile: PatientNameFields | null | undefined,
  embeddedName?: string | null
): string | undefined => {
  const identifier = profile?.demographic_string?.trim();
  const full = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim();
  const embedded = embeddedName?.trim();
  return identifier || full || embedded || undefined;
};

export const hasPatientNameFields = (
  profile: PatientNameFields | null | undefined
): profile is PatientNameFields =>
  Boolean(
    profile?.first_name?.trim() ||
    profile?.last_name?.trim() ||
    profile?.demographic_string?.trim()
  );
